import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import { ButtonGhost } from '../components/Button';

const Section = styled.section`
  padding: 140px 80px;
  @media (max-width: 900px) { padding: 80px 24px; }
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: flex-end;
  margin-bottom: 80px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 8vw, 100px);
  line-height: 0.9;
  letter-spacing: 0.02em;

  .accent {
    background: ${({ theme }) => theme.gradients.brand};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const HeaderDesc = styled.p`
  font-size: 16px;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  overflow: hidden;
  transition: all 0.4s;
  display: flex;
  flex-direction: column;
  min-height: 420px;

  &:hover {
    border-color: ${({ $borderHover }) => $borderHover};
    transform: translateY(-5px);
    box-shadow: 0 30px 80px ${({ $shadowColor }) => $shadowColor};
  }
`;

const CardImage = styled.div`
  height: 210px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardEmoji = styled.div`
  font-size: 72px;
  filter: drop-shadow(0 0 20px ${({ $glow }) => $glow});
  transition: transform 0.4s;
  ${Card}:hover & { transform: scale(1.1) translateY(-4px); }
`;

const CardBody = styled.div`
  padding: 32px 30px;
  @media (max-width: 480px) { padding: 24px 18px; }
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTag = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.3em;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 34px;
  letter-spacing: 0.04em;
  margin-bottom: 14px;
  color: #fff;
`;

const CardDesc = styled.p`
  font-size: 14px;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex: 1;
`;

const CardFooter = styled.div`
  margin-top: 26px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.05);
`;

const ecosystemMeta = [
  {
    icon: '⚙️',
    bg: 'linear-gradient(135deg, #001a00, #003a00)',
    emojiGlow: 'rgba(0,255,136,0.6)',
    tagColor: '#00FF88',
    borderColor: 'rgba(0,255,136,0.1)',
    borderHover: 'rgba(0,255,136,0.38)',
    shadowColor: 'rgba(0,255,136,0.1)',
    link: 'https://github.com/GueblyGames/guebly-engine/releases/latest',
  },
  {
    icon: '🛒',
    bg: 'linear-gradient(135deg, #1a0600, #3a0e00)',
    emojiGlow: 'rgba(196,90,58,0.6)',
    tagColor: '#C45A3A',
    borderColor: 'rgba(196,90,58,0.1)',
    borderHover: 'rgba(196,90,58,0.38)',
    shadowColor: 'rgba(196,90,58,0.1)',
    link: 'https://store.games.guebly.com.br',
  },
  {
    icon: '🚀',
    bg: 'linear-gradient(135deg, #000d1a, #001f3a)',
    emojiGlow: 'rgba(0,163,255,0.6)',
    tagColor: '#00A3FF',
    borderColor: 'rgba(0,163,255,0.1)',
    borderHover: 'rgba(0,163,255,0.38)',
    shadowColor: 'rgba(0,163,255,0.1)',
    link: 'https://launcher.games.guebly.com.br',
  },
];

export default function Ecosystem() {
  const { t } = useTranslation();
  const items = t('ecosystem.items', { returnObjects: true });

  return (
    <Section id="ecossistema">
      <Inner>
        <Header>
          <AnimatedSection direction="left">
            <SectionLabel>{t('ecosystem.sectionLabel')}</SectionLabel>
            <Title>
              {t('ecosystem.titlePrefix')}<br />
              <span className="accent">{t('ecosystem.titleAccent')}</span>
            </Title>
          </AnimatedSection>
          <AnimatedSection direction="right">
            <HeaderDesc>{t('ecosystem.headerDesc')}</HeaderDesc>
          </AnimatedSection>
        </Header>

        <Grid>
          {items.map((item, i) => (
            <AnimatedSection key={i} delay={i * 120}>
              <Card
                $borderColor={ecosystemMeta[i].borderColor}
                $borderHover={ecosystemMeta[i].borderHover}
                $shadowColor={ecosystemMeta[i].shadowColor}
              >
                <CardImage $bg={ecosystemMeta[i].bg}>
                  <CardEmoji $glow={ecosystemMeta[i].emojiGlow}>
                    {ecosystemMeta[i].icon}
                  </CardEmoji>
                </CardImage>
                <CardBody>
                  <CardTag $color={ecosystemMeta[i].tagColor}>{item.tag}</CardTag>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDesc>{item.desc}</CardDesc>
                  <CardFooter>
                    <ButtonGhost
                      href={ecosystemMeta[i].link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('ecosystem.learnMore')}
                    </ButtonGhost>
                  </CardFooter>
                </CardBody>
              </Card>
            </AnimatedSection>
          ))}
        </Grid>
      </Inner>
    </Section>
  );
}
